import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRepeat } from '@fortawesome/pro-light-svg-icons'

import {
  Button, FileInput, Form, Image, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import EntitySelector from '@components/EntitySelector'

import type { TagModel } from '@models/types'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import useCreateOrEditTagModal from './hooks/useCreateOrEditTagModal'

const MODAL_KEY = 'CreateOrEditTagModal'

type ModalCallbacks = {
  closeModal: () => void,
  createTag: (payload: HandleCallbackActionParams) => Promise<object>,
  updateTag: (payload: HandleCallbackActionParams) => Promise<object>,
}

type CreateOrEditTagModalProps = {
  callbacks: ModalCallbacks,
  createFn: Function,
  entityState: Partial<TagModel>,
  updateFn: Function,
}

const createOrEditTag = (params: CreateOrEditTagModalProps) => {
  const {
    callbacks, createFn, entityState, updateFn,
  } = params

  const { closeModal, createTag, updateTag } = callbacks || {}
  const [text, actionFn] = entityState.id ? ['Updated', updateFn] : ['Created', createFn]

  const tagPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: defaultRequestOptions.tag,
    toastText: `Tag ${text}`,
  }

  // @ts-expect-error TS2345
  if (entityState.id) return updateTag(tagPayload)

  // @ts-expect-error TS2345
  return createTag(tagPayload)
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const CreateOrEditTagModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { tag }: { tag?: TagModel} = modalPayload

  const createOrEditTagPayload = useCreateOrEditTagModal(tag)

  const {
    callbacks: {
      createTag: createFn,
      setState,
      updateTag: updateFn,
    },
    formPayload,
    imageUrl,
    isBrandUser,
    loading,
    showImage,
  } = createOrEditTagPayload

  const {
    entityState,
    entityState: {
      id,
      global,
      hidden,
      owner_id,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = formPayload

  const updateOrCreateButtonText = id ? 'Save Tag' : 'Create Tag'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Add'} titleSecondLine="Tag" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="Global" marginTop="large">
            <select
              data-value-type="boolean"
              disabled={isBrandUser}
              name="global"
              value={global}
              onChange={(e) => {
                // @ts-expect-error TS2345
                handlers.onChange(e)

                if (e.target.value === 'true' && owner_id){
                  setEntityState({ owner_id: null, owner_type: '' })
                }
              }}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          {!global && (
            <EntitySelector
              callbacks={{
                selectItem: brand => setEntityState({ owner_id: brand ? brand.id : null, owner_type: 'Brand' }),
              }}
              disabled={isBrandUser}
              entityKey="brands"
              errorMessage={errors.owner_id}
              label="* Brand"
              marginTop="large"
              selectedItemId={owner_id}
            />
          )}

          <Form.Field label="Active" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select data-value-type="boolean" name="hidden" value={hidden} {...handlers}>
              <option value="true">No</option>
              <option value="false">Yes</option>
            </select>
          </Form.Field>

          {!showImage && (
            <Form.Field label="Image" marginTop="large">
              <FileInput
                input={{
                  onChange: (fileData: string, fileObject: { name: string }) => (
                    setEntityState({ image_file: fileData, image_file_name: fileObject.name })
                  ),
                }}
              />
            </Form.Field>
          )}

          {showImage && (
            <>
              <Form.Field label="Image" marginTop="large">
                <Image
                  backgroundSize="contain"
                  borderRadius={5}
                  boxProps={{ border: '1px solid', borderColor: 'lineColor' }}
                  height={200}
                  url={imageUrl}
                  width="100%"
                />
              </Form.Field>

              <Form.Field label="More Options" marginTop="large">
                <Button
                  onClick={() => setState({ showImage: false })}
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faRepeat} />}
                  size="medium"
                >
                  Replace Image
                </Button>
              </Form.Field>
            </>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={loading}
          onClick={() => createOrEditTag({
            entityState, callbacks, createFn, updateFn,
          })}
          size="large"
        >
          {saveEnabled ? updateOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditTagModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
